<template>
  <div ref="panelRef" class="feedback-panel">
    <div class="feedback-panel__card">
      <!-- 选择问题分类 -->
      <section class="feedback-panel__section">
        <SectionLabel :text="$t('选择问题分类')" required />
        <FeedbackTypeSelector v-model="selectedType" :types="feedbackTypes" />
      </section>

      <!-- 输入问题反馈 -->
      <section class="feedback-panel__section">
        <SectionLabel :text="$t('输入问题反馈')" required :tip="$t('(内容介于20~200字)')" />

        <div class="feedback-input">
          <textarea
            v-model="content"
            class="feedback-input__textarea"
            name="feedbackContent"
            :maxlength="MAX_LENGTH"
            :placeholder="$t('请详细描述您的问题或建议,被采纳后将获得更多奖励~')" />
          <span class="feedback-input__count">{{ content.length }}/{{ MAX_LENGTH }}</span>
        </div>
        <div class="feedback-input__upload">
          <div class="upload-area">
            <!-- 上传中的图片 -->
            <div v-for="file in uploadingFiles" :key="file.previewUrl" :class="styles.previewItem">
              <img :src="file.previewUrl" :class="styles.previewImage" />
              <div :class="[styles.previewMask, styles.maskLoading]">
                <SvgIcon name="svg/loading.svg" width="20px" />
              </div>
            </div>

            <!-- 已完成的图片 -->
            <ImagePreview
              v-if="completedFiles.length > 0"
              :src="completedFiles.map((f) => f.previewUrl)"
              :listClass="styles.previewList"
              :itemClass="styles.previewImage"
              :enableDelete="true"
              @delete="handleImageDelete" />

            <button
              v-if="fileList.length < MAX_FILES"
              type="button"
              class="upload-area__add"
              :aria-label="$t('上传图片')"
              @click="triggerFileInput">
              <SvgIcon name="svg/cs_upload_add.svg" class="upload-area__icon" size="36px" />
            </button>

            <span class="upload-area__count">{{ fileList.length }}/{{ MAX_FILES }}</span>
          </div>

          <p class="feedback-input__tip">
            {{ $t('文件格式为png、jpg、jpeg且文件大小不可超过{maxSize}KB', { maxSize }) }}
          </p>

          <input
            ref="fileInputRefID"
            type="file"
            name="feedbackImages"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            multiple
            hidden
            @change="handleFileChange" />
        </div>
      </section>

      <!-- 提交 -->
      <button
        type="button"
        class="feedback-panel__submit"
        :disabled="!canSubmit || isSubmitting"
        @click="handleSubmit">
        <SvgIcon v-if="isSubmitting" name="svg/loading.svg" size="20px" />
        <span v-else>{{ $t('提交反馈') }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFeedbackTypes } from '@/composables/useFeedbackTypes'
import { useToken } from '@/composables/useToken'
import { useImageUpload } from './useImageUpload'
import { addFeedback } from '@/api/service'
import SvgIcon from '@/components/SvgIcon.vue'
import ImagePreview from '@/components/ImagePreview/ImagePreview.vue'
import SectionLabel from './components/SectionLabel.vue'
import FeedbackTypeSelector from './components/FeedbackTypeSelector.vue'
import relativeToast from '@/utils/relativeToast'
import { showLogin } from '@/utils/authDialog'

/** 反馈内容最小/最大字数 */
const MIN_LENGTH = 20
const MAX_LENGTH = 200
/** 最多上传图片数 */
const MAX_FILES = 3

const { t } = useI18n()
const { isLoggedIn } = useToken()
const { feedbackTypes, fetchFeedbackTypes } = useFeedbackTypes()

/** 相对定位 toast 的根元素 */
const panelRef = ref<HTMLElement>()
const fileInputRef = useTemplateRef<HTMLInputElement>('fileInputRefID')

const { fileList, maxSize, triggerFileInput, removeFile, handleFileChange } = useImageUpload(
  fileInputRef,
  panelRef
)

const selectedType = ref<number>()
const content = ref('')
const isSubmitting = ref(false)

const canSubmit = computed(
  () =>
    !!selectedType.value && content.value.length >= MIN_LENGTH && content.value.length <= MAX_LENGTH
)

/** 上传中的图片（不可预览） */
const uploadingFiles = computed(() => fileList.value.filter((f) => f.status === 'uploading'))
/** 已完成的图片 */
const completedFiles = computed(() => fileList.value.filter((f) => f.status === 'done'))

/** 删除已完成图片：completedFiles 索引映射回 fileList 索引 */
const handleImageDelete = (index: number) => {
  const target = completedFiles.value[index]
  if (!target) return
  const fileIndex = fileList.value.findIndex((f) => f === target)
  if (fileIndex !== -1) removeFile(fileIndex)
}

const resetForm = () => {
  selectedType.value = undefined
  content.value = ''
  fileList.value.forEach((file) => {
    if (file.previewUrl) URL.revokeObjectURL(file.previewUrl)
  })
  fileList.value = []
}

const handleSubmit = async () => {
  if (!canSubmit.value || !selectedType.value) return

  isSubmitting.value = true
  try {
    const imageUrls = completedFiles.value.map((f) => f.resUrl)

    const res = await addFeedback({
      questionType: selectedType.value,
      content: content.value,
      fileUrl: imageUrls.length > 0 ? imageUrls : undefined
    })

    if (res.isSuccess) {
      relativeToast.info(t('提交成功'), { container: panelRef.value!, position: 'center' })
      resetForm()
    }
  } catch (error) {
    console.error('Failed to submit feedback:', error)
  } finally {
    isSubmitting.value = false
  }
}

onMounted(() => {
  if (!isLoggedIn.value) showLogin()
  fetchFeedbackTypes()
})
</script>

<style scoped lang="scss">
.feedback-panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 600px;
  padding: 20px;

  &__card {
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex: 1;
    min-height: 0;
    padding: 16px 10px 10px;
    border-radius: 12px;
    background: var(--tr-01, #282828);
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: 10px;

    &:nth-child(2) {
      flex: 1;
      min-height: 100px;
    }
  }

  &__submit {
    flex-shrink: 0;
    height: 45px;
    border: none;
    border-radius: 10px;
    background: linear-gradient(90deg, #d6a443 0%, #c07526 72%, #a15816 100%);
    color: #fff;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

.feedback-input {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  border-radius: 10px;
  background: #333;
  overflow: hidden;

  &__textarea {
    flex: 1;
    min-height: 100px;
    padding: 12px;
    border: none;
    background: transparent;
    color: #fff;
    font-size: 13px;
    line-height: 18px;
    resize: none;
    outline: none;
    font-family: 'Inter';
    border-radius: 10px;
    &::placeholder {
      color: rgba(255, 255, 255, 0.6);
    }
  }

  &__count {
    align-self: flex-end;
    padding: 0 12px 8px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.5);
  }

  &__tip {
    margin: 0;
    padding: 0 12px 9px;
    font-size: 12px;
    line-height: 18px;
    color: var(--yellow, #ffee30);
  }
}

.upload-area {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 9px 12px;

  &__add {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: none;
    border-radius: 6px;
    background: none;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.85;
    }
  }

  &__icon {
    width: 36px;
    height: 36px;
  }

  &__count {
    font-size: 12px;
    color: var(--font-color, #fff);
  }
}
</style>

<style lang="scss" module="styles">
.previewList {
  display: flex;
  gap: 8px;
}

.previewItem {
  position: relative;
  width: 36px;
  height: 36px;
}

.previewImage {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  object-fit: cover;
}

.previewMask {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.3);
}

.maskLoading {
  opacity: 1;
}
</style>
